import React, { useState, useEffect, useMemo } from 'react';
import { getPlayers, getMatches } from '../services/api';
import type { Player, Match } from '../services/api';
import Matrix from './Matrix';
import { calculateMatricesFromMatches } from '../utils/matrixUtils';
import eventBus from '../utils/eventBus';

const Statistics: React.FC = () => {
  const [todaysMatches, setTodaysMatches] = useState<Match[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const [playersData, matchesData] = await Promise.all([
        getPlayers(),
        getMatches(false, today)
      ]);
      
      setAllPlayers(playersData);
      setTodaysMatches(matchesData);
      setError(null);
    } catch (err) {
      setError('Failed to load statistical data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    eventBus.on('dataChanged', fetchData);
    return () => {
        eventBus.remove('dataChanged', fetchData);
    }
  }, []);

  const attendingPlayers = useMemo(() => {
    if (!Array.isArray(allPlayers)) return [];
    return allPlayers.filter(p => p.status !== 'absent');
  }, [allPlayers]);

  const { partnerMatrix, opponentMatrix } = useMemo(() => {
    if (!Array.isArray(todaysMatches) || !Array.isArray(attendingPlayers)) {
        return { partnerMatrix: {}, opponentMatrix: {} };
    }
    return calculateMatricesFromMatches(todaysMatches, attendingPlayers);
  }, [todaysMatches, attendingPlayers]);

  if (loading) return <div>Loading statistics...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div style={{ marginTop: '2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>Today's Statistics</h2>
      </div>
      <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
        <Matrix title="Partner Matrix (Today)" matrixData={partnerMatrix} players={attendingPlayers} />
        <Matrix title="Opponent Matrix (Today)" matrixData={opponentMatrix} players={attendingPlayers} />
      </div>
    </div>
  );
};

export default Statistics;